import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import PlusIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PlusIconPath";
import { useTranslation } from "@coworker/locales";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import React, { useRef } from "react";
import { useUID } from "../../../core/auth/useLoggedInUser";
import { Camera } from "../../RoomSettings/Atoms/Icons/Camera";
import { CheckmarkCircle } from "../../RoomSettings/Atoms/Icons/CheckmarkCircle";
import { SimpleListContainer } from "../../RoomSettings/Molecules/SimpleListContainer";
import { PaddedContainer } from "../../RoomSettings/styles/styles";

import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { useUploadImageToStorage } from "@coworker/apprestructured/src/tasks/hooks/useUploadImageToStorage";
import { useCreateNote } from "../../../hooks/useCreateNote";

interface ProofOfCompletionProps {
  value: {
    proof_of_completion: boolean;
    isDone: boolean;
    setIsDone: React.Dispatch<React.SetStateAction<boolean>>;
    state: string;
    id: string;
  };
}

export default function ProofOfCompletion({ value }: ProofOfCompletionProps) {
  const uid = useUID();
  const fileInput = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const createNote = useCreateNote(value.id);

  const updateTask = useUpdateTaskMutation(true);
  const handleButtonClick = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const isChina = isChineseEnvironment();

  const uploadImageToStorage = useUploadImageToStorage();

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let environment;
    if (isChina === true) {
      environment = "alicloud";
    } else {
      environment = "gcp";
    }

    if (!event.target.files) {
      return;
    }

    const fileUploaded = event.target.files[0];
    if (!fileUploaded) return;
    const data = await uploadImageToStorage.mutateAsync({ file: fileUploaded });
    if (!data) return;

    const imageToAdd = {
      path: `images/notes/${data.file_name}`,
      folder: "images/notes",
      file_name: data.file_name,
      provider: environment,
      creator_id: uid,
      created_at: Date.now(),
    };
    if (!imageToAdd) return;
    await createNote.mutateAsync({
      data: { text: t("proofOfCompletionString"), creator_id: uid },
      images: [imageToAdd],
    });
    await updateTask.mutateAsync({
      data: { proof_of_completion: true },
      taskId: value.id,
    });
    value.setIsDone(true);
  };

  return (
    <>
      {value.state === "ASSIGNED" && (
        <>
          <SimpleListContainer
            headline={t("proofOfCompletionString")}
            leftcontent={<Camera />}
          />
          <PaddedContainer $py={"8px"} $px={"16px"}>
            {(value.isDone || value.proof_of_completion) && (
              <div
                style={{
                  display: "flex",
                  color: "var(--green)",
                  marginLeft: "15%",
                  gap: "10px",
                }}
              >
                <CheckmarkCircle />
                <p>{t("proofOfCompletionSubmittedString")}</p>
              </div>
            )}
            <FixaButton
              loading={uploadImageToStorage.isPending}
              text={t("addPhotoString")}
              ssrIcon={PlusIconPath}
              onClick={handleButtonClick}
              style={{ marginLeft: "15%", marginTop: "15px" }}
            />
            <input
              type="file"
              accept="image/png, image/jpeg"
              ref={fileInput}
              style={{ display: "none" }}
              onChange={handleChange}
            />
          </PaddedContainer>
        </>
      )}
    </>
  );
}
